import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Camera, Trash2 } from 'lucide-react'
import { listSnapshots, deleteSnapshot } from './snapshotsApi'
import type { Snapshot } from '../../types/snapshot'

interface SnapshotsListProps {
  clientId: number
  reportId: number
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR')
}

export function SnapshotsList({ clientId, reportId }: SnapshotsListProps) {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [loading, setLoading] = useState(true)
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listSnapshots(clientId, reportId)
      .then((list) => {
        if (cancelled) return
        setSnapshots(list)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [clientId, reportId])

  function handleDeleteClick(id: number) {
    setConfirmingDeleteId(id)
  }

  async function handleConfirmDelete(id: number) {
    await deleteSnapshot(id)
    setSnapshots((prev) => prev.filter((s) => s.id !== id))
    setConfirmingDeleteId(null)
  }

  if (loading) {
    return <p className="px-1 py-2 text-sm text-[#8B8FA7]">Chargement…</p>
  }

  if (snapshots.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
        <Camera size={22} className="text-[#C8CBD8]" strokeWidth={1.5} />
        <p className="text-sm font-medium text-[#1B2559]">Aucun snapshot</p>
        <p className="text-xs text-[#8B8FA7]">Crée un snapshot depuis un rapport pour figer un envoi.</p>
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-2">
      {snapshots.map((snapshot) => {
        const confirming = confirmingDeleteId === snapshot.id
        return (
          <li
            key={snapshot.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-light-border bg-light-surface px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[#1B2559]">{snapshot.name || 'Sans nom'}</p>
              <p className="text-xs text-[#8B8FA7]">
                {snapshot.periodStart} → {snapshot.periodEnd}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <span className="rounded-full bg-orion-primary-soft px-2 py-0.5 text-[11px] font-medium text-orion-primary">
                  {snapshot.accessMode === 'public_link' ? 'Lien public' : 'Privé'}
                </span>
                <span className="text-[11px] text-[#8B8FA7]">{formatDate(snapshot.createdAt)}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Link
                to={`/snapshots/${snapshot.id}`}
                className="rounded-md px-2.5 py-1.5 text-[13px] font-medium text-orion-primary hover:bg-orion-primary-soft"
              >
                Voir
              </Link>
              {confirming ? (
                <button
                  type="button"
                  onClick={() => handleConfirmDelete(snapshot.id)}
                  className="flex items-center gap-1 rounded-md bg-score-critical px-2.5 py-1.5 text-[13px] font-semibold text-white hover:opacity-90"
                >
                  Confirmer
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleDeleteClick(snapshot.id)}
                  className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-[#8B8FA7] hover:bg-white hover:text-score-critical"
                >
                  <Trash2 size={13} strokeWidth={1.75} />
                  Supprimer
                </button>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
